import axios from 'axios';
import getConfig from '@src/util/getConfig';
import { getSessionToken, isSessionTokenExpired } from '@descope/react-sdk';
import * as Sentry from '@sentry/react';
import { useSessionStore } from '@src/store';
import toast from 'react-hot-toast';
import { axiosDefaults } from '@src/apiFetch/axiosConfig.const';
import { trackEvent } from '@src/utils/trackEvent';
import { activateMaintenanceMode, isMaintenanceError } from '@src/store/maintenanceState';

/**
 * @type {AxiosInstance}
 */
const BACKEND_API = axios.create({
    // E2E isolated stack: when GATSBY_API_BASE_URL is set at build time (see
    // `e2e:isolated:build`), point the API at the local pglite e2e server
    // instead of the Directus-generated appConfig base. In production that env
    // is unset, so this falls back to getConfig().api_base_url unchanged.
    baseURL: process.env.GATSBY_API_BASE_URL || `${getConfig().api_base_url}`,
});
Object.assign(BACKEND_API.defaults, axiosDefaults);

BACKEND_API.interceptors.request.use(
    async (config) => {
        const xanoBranch = getConfig().xano_branch;
        if (!xanoBranch) {
            return config;
        }

        return {
            ...config,
            headers: {
                ...config.headers,
                'X-Branch': xanoBranch,
            },
        };
    },
);

let lastToast = 0;
const triggerSessionExpired = () => {
    if (Date.now() - lastToast > 5000) {
        toast.error('Session expired');
        lastToast = Date.now();
    }
    useSessionStore.getState().logout();
};

BACKEND_API.interceptors.request.use(
    async (config) => {
        if (config.skipAuth || config.headers.Authorization) {
            return config;
        }
        let accessToken = getSessionToken();

        if (accessToken && isSessionTokenExpired()) {
            await useSessionStore.getState().refresh();
            accessToken = getSessionToken();
            if (!accessToken || isSessionTokenExpired()) {
                throw new Error('Session refresh failed');
            }
        }

        if (!accessToken) {
            throw new Error('Unable to make secure request, current session does not have a token set.');
        }

        // eslint-disable-next-line no-param-reassign
        config.headers = {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`,
        };

        return config;
    },
    (error) => {
        Sentry.captureException(error);
        console.error('error: ', error);
        trackEvent('session_refresh_failed', {
            error_type: String(error?.response?.status ?? 'unknown'),
        });
        setTimeout(triggerSessionExpired, 0); // Next frame
        return Promise.reject(error);
    },
);

BACKEND_API.interceptors.response.use(
    (response) => response,
    (error) => {
        // Backend is soft-frozen (maintenance mode) — flag this tab (and broadcast
        // to all other open tabs) before any session-expired handling;
        if (isMaintenanceError(error)) {
            activateMaintenanceMode();
            return Promise.reject(error);
        }
        if (!error.response && error.request) {
            Sentry.captureException(new Error(`Network Error: ${error.config?.url}`), {
                extra: {
                    url: error.config?.url,
                    method: error.config?.method,
                    message: error.message || 'No response received',
                },
            });
        }
        const { message } = error.response?.data || {};
        if (message === 'Session expired' || message === 'The token expired.') {
            setTimeout(triggerSessionExpired, 0); // Next frame
        }
        return Promise.reject(error);
    },
);

export {
    BACKEND_API,
};
